import React from 'react';
import { Link } from 'react-router-dom';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import 'react-lazy-load-image-component/src/effects/blur.css';
import { buildInternalLinkSections } from '../../utils/seo/internalLinks';
import { buildPropertyPath } from '../../utils/seo/urlHelpers';
import { getPropertyTypeLabel } from '../../constants/propertyTypes';

function formatPrice(property) {
  const value = property.purpose === 'rent' ? property.rent : property.price;
  if (!value) return '';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  }).format(value);
}

function getPropertyImage(property) {
  return property.coverImage || property.images?.[0] || null;
}

function PropertyLinkCard({ property }) {
  const imageSrc = getPropertyImage(property);
  const price = formatPrice(property);
  const title = property.title || getPropertyTypeLabel(property.type);

  return (
    <Link
      to={buildPropertyPath(property)}
      className="group block rounded-lg border border-gray-100 bg-white overflow-hidden shadow-sm hover:shadow-md hover:border-gray-200 transition-all"
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-gray-100">
        {imageSrc ? (
          <div className="w-full h-full transform transition-transform duration-500 ease-out group-hover:scale-105">
            <LazyLoadImage
              src={imageSrc}
              alt={title}
              className="w-full h-full object-cover"
              wrapperClassName="w-full h-full block"
              effect="blur"
              placeholder={
                <div className="w-full h-full bg-gray-200 animate-pulse" />
              }
            />
          </div>
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-300">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-12 h-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
          </div>
        )}
        {price && (
          <div className="absolute bottom-2 left-2 right-2">
            <span className="inline-block text-white text-sm font-medium bg-gray-900/60 px-2 py-0.5 rounded backdrop-blur-sm">
              {price}
            </span>
          </div>
        )}
        {property.purpose && (
          <div className="absolute top-2 left-2">
            <span className="bg-gray-900/50 text-white px-2 py-0.5 rounded text-xs backdrop-blur-sm">
              {property.purpose === 'sale' ? 'Venda' : 'Aluguel'}
            </span>
          </div>
        )}
      </div>
      <div className="p-3">
        <h3 className="font-medium text-gray-900 line-clamp-2 group-hover:text-[color:var(--primary-color,#3B82F6)] transition-colors">
          {title}
        </h3>
        <p className="text-sm text-gray-500 line-clamp-1 mt-1">
          {[property.neighborhood, property.city].filter(Boolean).join(', ')}
        </p>
      </div>
    </Link>
  );
}

/**
 * Seções de links internos automáticos para página de imóvel.
 */
export default function PropertyInternalLinks({ property, allProperties = [] }) {
  const sections = buildInternalLinkSections(property, allProperties);

  if (!sections.length) return null;

  return (
    <aside className="space-y-10" aria-label="Links relacionados">
      {sections.map((section) => (
        <section key={section.id}>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-4 mb-4">
            <h2 className="text-xl font-semibold text-gray-900">{section.title}</h2>
            {section.listingPath && (
              <Link
                to={section.listingPath}
                className="text-sm font-medium hover:underline whitespace-nowrap self-end sm:self-auto"
                style={{ color: 'var(--primary-color, #3B82F6)' }}
              >
                Ver todos
              </Link>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {section.links.map((prop) => (
              <PropertyLinkCard key={prop.id} property={prop} />
            ))}
          </div>
        </section>
      ))}
    </aside>
  );
}
